'use client';

import {createContext, useCallback, useMemo, useRef, useState} from 'react';
import {startOfToday} from 'date-fns';
import styled from '@emotion/styled';

import {startOfDay} from 'common';

import {CalendarMode} from 'Calendar';

import {DateInput} from './DateInput';
import {Calendar} from './Calendar';

import {DEFAULT_FORMAT} from './types';

import type {RangeCalendarValue} from 'Calendar';
import type {BaseDatepickerProps, DatepickerProps, RangeDatepickerProps, DatepickerContextProps} from './types';

export const DatepickerContext = createContext<DatepickerContextProps>({} as never);

const Container = styled.div`
  position: relative;
`;

const BaseDatepicker: React.FC<BaseDatepickerProps> = props => {
  const {
    mode,
    value,
    onChange,
    formatStr,
    calendarProps,

    isOpen: parentIsOpen,
    setIsOpen: parentSetIsOpen,

    referenceDate = startOfToday(),
    width = '100%',
    qa = 'datepicker',

    ...rest
  } = props;

  const targetRef = useRef<HTMLInputElement>(null);
  const [isOpenIn, setIsOpenIn] = useState<boolean>(false);
  const isOpen = parentIsOpen ?? isOpenIn;
  const setIsOpen = parentSetIsOpen ?? setIsOpenIn;

  const context: DatepickerContextProps = {
    mode,
    value,
    onChange,
    formatStr,
    referenceDate,
    isOpen,
    setIsOpen,
  };

  const calProps = {
    calendarProps,
    targetRef,
  };

  return (
    <DatepickerContext.Provider value={context}>
      <Container style={{width}}>
        <DateInput ref={targetRef} qa={qa} {...rest} />
        <Calendar {...calProps} />
      </Container>
    </DatepickerContext.Provider>
  );
};

export const Datepicker: React.FC<DatepickerProps> = props => {
  const value = useMemo(() => [props.value ? startOfDay(props.value) : undefined, undefined], [props.value]);

  const onChange = useCallback<BaseDatepickerProps['onChange']>(
    newValue => props.onChange(newValue[0]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange]
  );

  const overrideProps = {
    ...props,

    value,
    onChange,

    mode: CalendarMode.Single,

    formatStr: props.formatStr ?? DEFAULT_FORMAT,
  } as BaseDatepickerProps;

  return <BaseDatepicker {...overrideProps} />;
};

export const RangeDatepicker: React.FC<RangeDatepickerProps> = props => {
  const value = useMemo<RangeCalendarValue>(() => {
    const [rangeStart, rangeEnd] = props.value ?? [];

    return [rangeStart ? startOfDay(rangeStart) : undefined, rangeEnd ? startOfDay(rangeEnd) : undefined];
  }, [props.value]);

  const format = `${props.formatStr ?? DEFAULT_FORMAT} — ${props.formatStr ?? DEFAULT_FORMAT}`;

  return <BaseDatepicker mode={CalendarMode.Range} {...props} value={value} formatStr={format} />;
};
